import { motion, useInView, animate } from "framer-motion";
import { useRef, useEffect, useState } from "react";
import { Star, ShieldCheck, MapPin, Wrench, UserCheck, Briefcase } from "lucide-react";
import { useBrand } from "../../context/SiteSettingsContext";
import { splitStat } from "../../lib/siteConfig";

/**
 * The two figures the business actually updates — jobs completed and the
 * Google rating — come from website settings so they can be changed without a
 * deploy. The rest describe how the company operates rather than a count, so
 * they are not editable content.
 */
function buildStats(brand) {
  const jobs = splitStat(brand?.stats?.jobs, "+");
  const rating = splitStat(brand?.stats?.rating, "★");

  return [
    { icon: Briefcase, value: jobs.value, suffix: jobs.suffix, label: "Jobs Completed" },
    {
      icon: Star,
      value: rating.value,
      suffix: "★",
      label: "Google Rating",
      // Ratings read as "4.9", never "5"; keep the decimal even on a whole number.
      decimals: 1,
    },
    { icon: ShieldCheck, value: "100", suffix: "%", label: "Insured Coverage" },
    { icon: MapPin, value: "1", prefix: "Islandwide", label: "Singapore Service", isText: true },
    { icon: Wrench, value: "20", suffix: "+", label: "Professional Tools" },
    { icon: UserCheck, value: "100", suffix: "%", label: "Trained Team" },
  ];
}

function Counter({ value, decimals = 0, suffix = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-15% 0px" });
  const [display, setDisplay] = useState("0");
  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, Number(value), {
      duration: 2,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (v) => setDisplay(v.toFixed(decimals)),
    });
    return controls.stop;
  }, [inView, value, decimals]);
  return (
    <span ref={ref}>
      {display}
      <span className="text-emerald">{suffix}</span>
    </span>
  );
}

export default function TrustStats() {
  const brand = useBrand();
  const STATS = buildStats(brand);

  return (
    <section
      className="bg-ink border-y border-charcoal/70 py-12 md:py-16"
      data-testid="trust-stats"
    >
      <div className="container-emilio">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 md:gap-12">
          {STATS.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-10%" }}
                transition={{ duration: 0.6, delay: i * 0.05 }}
                className="flex flex-col gap-3"
              >
                <Icon className="w-5 h-5 text-emerald" />
                <div className="font-display font-extrabold text-white text-3xl md:text-4xl leading-none">
                  {s.isText ? (
                    <span className="text-white text-xl md:text-2xl">{s.prefix}</span>
                  ) : (
                    <Counter value={s.value} decimals={s.decimals} suffix={s.suffix} />
                  )}
                </div>
                <div className="text-xs md:text-sm text-mutedtext uppercase tracking-[0.14em]">
                  {s.label}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
